import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Stack, Typography, Paper, Button, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    IconButton, Avatar, Drawer, Divider, Chip
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AppSearch from "../../components/base/AppSearch";
import AppPagination from "../../components/base/AppPagination";
import ConfirmDialog from "../../components/base/ConfirmDialog";
import { MenuItem, FormControl, InputLabel, Select } from "@mui/material";
const VideoPacks = () => {
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [packs, setPacks] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    const [openModal, setOpenModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [selectedPack, setSelectedPack] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [formData, setFormData] = useState({
        id: null,
        name: '',
        price: '',
        description: '',
        token_amount: 0,
        status: 'active'
    });



    const apiUrl = process.env.REACT_APP_API_URL;
    const storageUrl = process.env.REACT_APP_STORAGE_URL;
    const token = localStorage.getItem("token");

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiUrl}/admin/video-packs/back/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPacks(response.data.result || response.data || []);
        } catch (err) {
            toast.error("Erreur de chargement des packs vidéo");
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleClose = () => {
        setOpenModal(false);
        // Pas besoin de gérer imagePreview ici
        setFormData({ id: null, name: '', price: '', description: '', token_amount: 0, status: 'active' });
    };



    const handleSubmit = async () => {
        try {
            setLoading(true);

            const payload = {
                name: formData.name,
                price: formData.price,
                description: formData.description,
                token_amount: formData.token_amount,
                status: formData.status
            };

            const method = isEditMode ? 'put' : 'post';
            const url = isEditMode
                ? `${apiUrl}/admin/video-packs/${formData.id}`
                : `${apiUrl}/admin/video-packs/back/store`;

            await axios({
                method: method,
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success(isEditMode ? "Pack mis à jour" : "Pack créé avec succès");
            handleClose();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || "Erreur lors de l'enregistrement");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${apiUrl}/admin/video-packs/${itemToDelete}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Pack supprimé");
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        } finally {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
        }
    };

    const filteredData = packs.filter(item =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // Helper pour les stats dans le drawer
    const StatBox = ({ icon, label, value, color }) => (
        <Paper elevation={0} sx={{ p: 1.5, flex: 1, borderRadius: '12px', bgcolor: '#F4F7FE', border: '1px solid #E0E5F2', textAlign: 'center' }}>
            <Box sx={{ color: color, display: 'flex', justifyContent: 'center', mb: 0.5 }}>{icon}</Box>
            <Typography variant="body1" sx={{ fontWeight: 800, color: '#1B2559' }}>{value}</Typography>
            <Typography variant="caption" sx={{ color: '#A3AED0', fontWeight: 600, display: 'block' }}>{label}</Typography>
        </Paper>
    );

    return (
        <Box sx={{ p: 3, bgcolor: "#F4F7FE", minHeight: "100vh" }}>
            {/* HEADER */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#1B2559" }}>Packs Jetons </Typography>
                <Stack direction="row" spacing={2}>
                    <AppSearch value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher un pack..." />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => { setIsEditMode(false); setOpenModal(true); }}
sx={{
                                bgcolor: "#4318FF",
                                borderRadius: "20px", // Plus arrondi pour l'effet "pilule"
                                px: 8,               // Réduit le padding (12 était trop large)
                                py: 1.5,             // Ajuste le padding vertical au lieu d'une hauteur fixe
                                textTransform: 'none',
                                fontSize: '14px',    // Taille de police standard dashboard
                                fontWeight: 600,     // Pour plus de lisibilité
                                whiteSpace: 'nowrap', // Force le texte sur une seule ligne
                                boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)', // Optionnel: petite ombre portée
                                "&:hover": {
                                    bgcolor: "#3311CC",
                                    boxShadow: 'none'
                                }
                            }}                    >
                        Créer un pack
                    </Button>
                </Stack>
            </Stack>

            {/* TABLEAU */}
            <Paper sx={{ borderRadius: "24px", boxShadow: "0px 20px 50px rgba(112, 144, 176, 0.08)", overflow: "hidden" }}>
                {loading ? (
                    <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: "#F4F7FE" }}>
                                <TableRow>
                                    <TableCell sx={{ color: "#A3AED0", fontWeight: "700", pl: 3 }}>PACK</TableCell>
                                    <TableCell sx={{ color: "#A3AED0", fontWeight: "700" }}>PRIX</TableCell>
                                    <TableCell sx={{ color: "#A3AED0", fontWeight: "700" }}>JETONS</TableCell>
                                    <TableCell sx={{ color: "#A3AED0", fontWeight: "700" }}>STATUT</TableCell>
                                    <TableCell align="right" sx={{ color: "#A3AED0", fontWeight: "700", pr: 3 }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentItems.map((row) => (
                                    <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}>
                                        <TableCell sx={{ pl: 3 }}>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Avatar src={`${storageUrl}/${row.photo}`} variant="rounded" sx={{ width: 48, height: 48, borderRadius: '12px' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 800, color: "#1B2559" }}>{row.name}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontWeight: 800, color: "#4318FF" }}>{row.price} €</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={`${row.token_amount} Jetons`} size="small" sx={{ fontWeight: 700, bgcolor: '#E2E8F0', color: '#1B2559' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.status === 'active' ? "ACTIF" : "INACTIF"} // Texte en majuscules
                                                size="small"
                                                // Le petit point indicateur (Avatar)
                                                avatar={
                                                    <Box
                                                        sx={{
                                                            width: '6px !important',
                                                            height: '6px !important',
                                                            borderRadius: '50%',
                                                            // Couleur du point selon le statut (vert ou rouge)
                                                            bgcolor: row.status === 'active' ? '#05CD99' : '#EE5D50',
                                                            ml: '8px !important', // Espace à gauche du point
                                                        }}
                                                    />
                                                }
                                                sx={{
                                                    // Fond très clair (quasi transparent)
                                                    bgcolor: row.status === 'active' ? '#E6FAF5' : '#FFF5F5',
                                                    // Couleur du texte (vert ou rouge)
                                                    color: row.status === 'active' ? '#05CD99' : '#EE5D50',
                                                    fontWeight: 800, // Texte gras
                                                    fontSize: '11px', // Taille de police petite
                                                    borderRadius: '12px', // Bords très arrondis
                                                    height: '24px', // Hauteur du badge
                                                    border: 'none', // Pas de bordure
                                                    '& .MuiChip-label': {
                                                        pl: '6px', // Espace entre le point et le texte
                                                        pr: '10px', // Espace à droite du texte
                                                    },
                                                    // Masquer l'avatar par défaut de MUI s'il y en a un
                                                    '& .MuiChip-avatar': {
                                                        bgcolor: 'transparent',
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ pr: 3 }}>
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <IconButton onClick={() => { setSelectedPack(row); setDrawerOpen(true); }} sx={{ color: '#4318FF', bgcolor: '#F4F7FE' }} size="small">
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton onClick={() => { setIsEditMode(true); setFormData(row); setOpenModal(true); }} sx={{ color: '#4318FF', bgcolor: '#F4F7FE' }} size="small">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton onClick={() => { setItemToDelete(row.id); setDeleteDialogOpen(true); }} size="small">
                                                    <DeleteIcon color="error" fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <AppPagination
                    count={Math.ceil(filteredData.length / itemsPerPage)}
                    page={currentPage}
                    onChange={(e, v) => setCurrentPage(v)}
                />
            </Paper>

            {/* MODALE AJOUT/EDIT */}
            <Dialog open={openModal} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1B2559' }}>
                        {isEditMode ? "Modifier le pack" : "Créer un nouvel pack vidéo"}
                    </Typography>
                    {/* Sous-titre avec l'instruction */}
                    <Typography variant="body2" sx={{ color: '#A3AED0', mt: 0.5 }}>
                        Veuillez remplir tous les champs obligatoires marqués d'une
                        <span style={{ color: '#EE5D50', fontWeight: 'bold', marginLeft: '4px' }}>étoile rouge *</span>.
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            label={<span>Nom du pack <span style={{ color: '#EE5D50' }}>*</span></span>}
                            fullWidth

                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    borderRadius: '12px',
                                    backgroundColor: 'white !important',
                                    border: '1px solid #E0E5F2',
                                    '&:hover': { backgroundColor: 'white !important' },
                                    '&.Mui-focused': { backgroundColor: 'white !important', border: '1px solid #4318FF' }
                                }
                            }}
                        />

                        <Stack direction="row" spacing={2}>
                            <TextField
                                label={<span>Prix (€) <span style={{ color: '#EE5D50' }}>*</span></span>}
                                type="number"
                                fullWidth

                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        borderRadius: '12px',
                                        backgroundColor: 'white !important',
                                        border: '1px solid #E0E5F2',
                                        '&:hover': { backgroundColor: 'white !important' },
                                        '&.Mui-focused': { backgroundColor: 'white !important', border: '1px solid #4318FF' }
                                    }
                                }}
                            />
                            <TextField
                                label={<span>Nombre de jetons <span style={{ color: '#EE5D50' }}>*</span></span>}
                                type="number"
                                fullWidth

                                value={formData.token_amount}
                                onChange={(e) => setFormData({ ...formData, token_amount: e.target.value })}
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        borderRadius: '12px',
                                        backgroundColor: 'white !important',
                                        border: '1px solid #E0E5F2',
                                        '&:hover': { backgroundColor: 'white !important' },
                                        '&.Mui-focused': { backgroundColor: 'white !important', border: '1px solid #4318FF' }
                                    }
                                }}
                            />
                        </Stack>

                        <FormControl fullWidth >
                            <InputLabel>
                                Statut <span style={{ color: '#EE5D50' }}>*</span>
                            </InputLabel>
                            <Select
                                value={formData.status}
                                label={<span>Statut <span style={{ color: '#EE5D50' }}>*</span></span>}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        borderRadius: '12px',
                                        backgroundColor: 'white !important',
                                        border: '1px solid #E0E5F2',
                                        '&:hover': { backgroundColor: 'white !important' },
                                        '&.Mui-focused': { backgroundColor: 'white !important', border: '1px solid #4318FF' }
                                    }
                                }}
                            >
                                <MenuItem value="active">Actif</MenuItem>
                                <MenuItem value="inactive">Inactif</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label={
                                <span>
                                    Description <span style={{ color: '#EE5D50' }}>*</span>
                                </span>
                            }
                            multiline
                            rows={4}
                            fullWidth
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Décrivez le contenu de ce pack..."
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    borderRadius: '12px',
                                    backgroundColor: 'white !important',
                                    border: '1px solid #E0E5F2',
                                    '&:hover': { backgroundColor: 'white !important' },
                                    '&.Mui-focused': { backgroundColor: 'white !important', border: '1px solid #4318FF' }
                                }
                            }}
                        // Surtout ne pas mettre "required" ici
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: '1px solid #F4F7FE' }}>
                    <Button
                        onClick={handleClose}
                        sx={{ color: '#1B2559', fontWeight: 600, textTransform: 'none' }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        sx={{
                            bgcolor: '#4318FF',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 4,
                            '&:hover': { bgcolor: '#3311CC' }
                        }}
                    >
                        Enregistrer le pack
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DRAWER DÉTAILS - ENRICHI */}
            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
                sx={{ zIndex: 3000 }}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 500 },
                        border: 'none',
                        boxShadow: '-10px 0px 30px rgba(0,0,0,0.08)' // Ombre douce pour profondeur
                    }
                }}
            >
                {selectedPack && (
                    <Box sx={{ width: { xs: '100%', sm: 450 }, p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>Détails du Pack</Typography>
                            <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
                        </Stack>

                        <Box
                            sx={{
                                width: '100%',
                                height: 180,
                                borderRadius: '20px',
                                mb: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                // Un dégradé linéaire aux couleurs de votre application
                                background: 'linear-gradient(135deg, #4318FF 0%, #B09FFF 100%)',
                                boxShadow: '0 10px 20px rgba(67, 24, 255, 0.2)',
                                color: '#fff'
                            }}
                        >
                            <PlayCircleIcon sx={{ fontSize: 60, mb: 1, opacity: 0.8 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '1px' }}>
                                PACK JETONS VIDÉO
                            </Typography>
                        </Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1B2559' }}>{selectedPack.name}</Typography>
                            <Chip label={selectedPack.status} color={selectedPack.status === 'active' ? 'success' : 'default'} size="small" />
                        </Stack>
                        <Typography variant="h4" sx={{ color: '#4318FF', fontWeight: 800, mb: 3 }}>{selectedPack.price} €</Typography>

                        <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
                            <StatBox icon={<PlayCircleIcon fontSize="small" />} label="Jetons" value={selectedPack.token_amount} color="#4318FF" />
                            <StatBox icon={<ShoppingCartIcon fontSize="small" />} label="Ventes" value={selectedPack.pack_count_buy_video} color="#05CD99" />
                            <StatBox icon={<ReceiptLongIcon fontSize="small" />} label="Revenu" value={`${selectedPack.total_revenu_pack}€`} color="#EE5D50" />
                        </Stack>

                        <Divider sx={{ mb: 3 }} />

                        <Typography variant="subtitle2" sx={{ color: '#A3AED0', mb: 1 }}>DESCRIPTION</Typography>
                        <Paper sx={{ p: 2, bgcolor: '#F4F7FE', borderRadius: '12px', borderLeft: '4px solid #4318FF', mb: 4 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{selectedPack.description}</Typography>
                        </Paper>

                        <Typography variant="subtitle2" sx={{ color: '#A3AED0', mb: 1 }}>INFORMATIONS SYSTÈME</Typography>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <CalendarTodayIcon sx={{ color: '#A3AED0', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="caption" display="block" sx={{ color: '#A3AED0' }}>Créé le</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(selectedPack.created_at).toLocaleDateString()}</Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <CalendarTodayIcon sx={{ color: '#A3AED0', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="caption" display="block" sx={{ color: '#A3AED0' }}>Dernière mise à jour</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(selectedPack.updated_at).toLocaleDateString()}</Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    </Box>
                )}
            </Drawer>

            <ConfirmDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDelete}
                title="Supprimer le pack ?"
                message="Cette action supprimera définitivement ce pack vidéo."
            />
        </Box>
    );
};

export default VideoPacks;